import { LoggerService } from "@Front/Services/LoggerService/LoggerService";

/**
 * Helper function to get the correct filename for a downloaded file
 * If the blob is a PDF, forces .pdf extension regardless of original filename
 * @param blob The blob to download
 * @param originalFileName The original filename from the file metadata
 * @returns The filename to use for download
 */
export async function getDownloadFileName(blob: Blob, originalFileName: string): Promise<string> {
	// Check if blob is a PDF by reading the first bytes
	// blob.type may not be reliable, so we check the actual content
	const isPdf = await checkIfBlobIsPdf(blob);

	LoggerService.debug("🔍 [getDownloadFileName] Checking blob type", {
		originalFileName,
		blobType: blob.type,
		blobSize: blob.size,
		isPdf,
	});

	if (isPdf) {
		const baseName = originalFileName.replace(/\.[^.]+$/, "");
		const pdfFileName = `${baseName}.pdf`;
		LoggerService.debug("✅ [getDownloadFileName] Forcing PDF extension", {
			originalFileName,
			pdfFileName,
		});
		return pdfFileName;
	}
	// Otherwise, use original filename
	return originalFileName;
}

/**
 * Ensure PDF extension for a filename based on document status and content type.
 * For validated documents (VALIDATED, SENT, DOWNLOADED): force PDF extension and add " (validé)" before the extension.
 * SENT/DOWNLOADED = documents received from notary (validated before sending).
 * For other documents: force PDF extension if content type indicates PDF.
 *
 * @param fileName The filename to process
 * @param documentStatus The document status (e.g., "VALIDATED", "SENT", "DOWNLOADED")
 * @param contentType The Content-Type header value
 * @param blobType The blob.type value
 * @returns The filename with correct PDF extension (and " (validé)" for validated documents)
 */
const VALIDATED_STATUSES = ["VALIDATED", "SENT", "DOWNLOADED"] as const;
const VALIDATED_SUFFIX = " (validé)";

function isValidatedStatus(status: string | null | undefined): boolean {
	return !!status && VALIDATED_STATUSES.includes(status as (typeof VALIDATED_STATUSES)[number]);
}

function addValidatedSuffixToFileName(fileName: string): string {
	const baseName = fileName.replace(/\.[^.]+$/, "") || fileName;
	const nameWithSuffix = baseName.endsWith(VALIDATED_SUFFIX) ? baseName : `${baseName}${VALIDATED_SUFFIX}`;
	return `${nameWithSuffix}.pdf`;
}

function isPdfContentType(contentType: string | null | undefined, blobType: string | null | undefined): boolean {
	return !!contentType?.includes("application/pdf") || !!blobType?.includes("application/pdf");
}

function isZipContentType(contentType: string | null | undefined, blobType: string | null | undefined): boolean {
	return (
		!!contentType?.includes("application/zip") ||
		!!contentType?.includes("application/x-zip-compressed") ||
		!!blobType?.includes("application/zip") ||
		!!blobType?.includes("application/x-zip-compressed")
	);
}

/** Certified download (PDF + certificate): backend streams a ZIP archive. */
export function ensureZipExtension(fileName: string): string {
	if (fileName.toLowerCase().endsWith(".zip")) {
		return fileName;
	}
	const baseName = fileName.replace(/\.[^.]+$/, "") || fileName;
	return `${baseName}.zip`;
}

export function ensurePdfExtension(
	fileName: string,
	documentStatus: string | null | undefined,
	contentType: string | null | undefined,
	blobType: string | null | undefined,
): string {
	if (isValidatedStatus(documentStatus)) return addValidatedSuffixToFileName(fileName);
	if (isPdfContentType(contentType, blobType) && !fileName.toLowerCase().endsWith(".pdf")) {
		return (fileName.replace(/\.[^.]+$/, "") || fileName) + ".pdf";
	}
	return fileName;
}

/**
 * Process blob to verify PDF format and ensure PDF extension
 * Checks blob content and ensures correct extension based on document status
 *
 * @param blob The blob to process
 * @param fileName The current filename
 * @param contentType The Content-Type header value
 * @param documentStatus The document status (e.g., "VALIDATED")
 * @param originalFileName The original filename from metadata
 * @param contentDisposition The Content-Disposition header value
 * @returns The processed filename with correct PDF extension
 */
export type ProcessBlobForDownloadParams = {
	blob: Blob;
	fileName: string;
	contentType: string | null | undefined;
	documentStatus: string | null | undefined;
	originalFileName: string | null | undefined;
	contentDisposition: string | null | undefined;
};

const PROCESS_BLOB_VALIDATED_STATUSES = ["VALIDATED", "SENT", "DOWNLOADED"] as const;

type ResolvePdfFileNameForDownloadInput = {
	resolvedFileName: string;
	documentStatus: string | null | undefined;
	contentType: string | null | undefined;
	blobType: string;
	isPdfBlob: boolean;
};

function resolvePdfFileNameForDownload(input: ResolvePdfFileNameForDownloadInput): string {
	const { resolvedFileName, documentStatus, contentType, blobType, isPdfBlob } = input;
	const isValidated =
		!!documentStatus &&
		PROCESS_BLOB_VALIDATED_STATUSES.includes(documentStatus as (typeof PROCESS_BLOB_VALIDATED_STATUSES)[number]);
	let next = resolvedFileName;
	if (isValidated) {
		const fileNameBefore = next;
		next = ensurePdfExtension(next, documentStatus, contentType, blobType);
		if (fileNameBefore !== next) {
			LoggerService.info("🔍 [processBlobForDownload] Forcing PDF extension for validated document", {
				documentStatus,
				fileNameBefore,
				fileNameAfter: next,
				contentType,
				isPdfBlob,
			});
		}
	} else {
		next = ensurePdfExtension(next, documentStatus, contentType, blobType);
	}
	if (isValidated && !isPdfBlob) {
		LoggerService.warn("⚠️ [processBlobForDownload] Blob is not a PDF for validated document", {
			contentType,
			blobType,
			isPdfBlob,
			fileName: next,
			documentStatus,
		});
	}
	return next;
}

export async function processBlobForDownload(params: ProcessBlobForDownloadParams): Promise<string> {
	const { blob, fileName, contentType, documentStatus, originalFileName, contentDisposition } = params;
	const isPdfBlob = await checkIfBlobIsPdf(blob);
	const isZipBlob = await checkIfBlobIsZip(blob);
	const isZipByHeader = isZipContentType(contentType, blob.type);

	LoggerService.info("📥 [processBlobForDownload] Download response", {
		contentType,
		blobType: blob.type,
		blobSize: blob.size,
		isPdfBlob,
		isZipBlob,
		isZipByHeader,
		fileName,
		contentDisposition,
		originalFileName,
	});

	if (isZipBlob || isZipByHeader) {
		const zipFileName = ensureZipExtension(fileName);
		if (zipFileName !== fileName) {
			LoggerService.info("🔍 [processBlobForDownload] Keeping ZIP extension for certified archive download", {
				fileNameBefore: fileName,
				fileNameAfter: zipFileName,
				documentStatus,
			});
		}
		return zipFileName;
	}

	return resolvePdfFileNameForDownload({
		resolvedFileName: fileName,
		documentStatus,
		contentType,
		blobType: blob.type,
		isPdfBlob,
	});
}

/**
 * Extract filename from Content-Disposition header
 * Supports both formats: filename*=UTF-8''encoded and filename="quoted"
 * @param contentDisposition The Content-Disposition header value
 * @param defaultFileName The default filename to use if extraction fails
 * @returns The extracted filename or default filename
 */
export function extractFileNameFromContentDisposition(contentDisposition: string | null, defaultFileName: string): string {
	if (!contentDisposition) return defaultFileName;

	const match = /filename\*=UTF-8''([^;]+)|filename="([^"]+)"/i.exec(contentDisposition);
	if (match) {
		try {
			const extractedFileName = decodeURIComponent(match[1] ?? match[2] ?? defaultFileName);
			if (extractedFileName) {
				LoggerService.debug("✅ [extractFileNameFromContentDisposition] Extracted filename", {
					contentDisposition,
					extractedFileName,
					defaultFileName,
				});
				return extractedFileName;
			}
		} catch (decodeError) {
			LoggerService.warn("⚠️ [extractFileNameFromContentDisposition] Failed to decode filename from Content-Disposition", {
				contentDisposition,
				error: decodeError,
				defaultFileName,
			});
		}
	}

	return defaultFileName;
}

/**
 * Check if a blob is a PDF by reading the first 4 bytes
 * @param blob The blob to check
 * @returns Promise<boolean> True if the blob starts with "%PDF"
 */
/**
 * ZIP local file header starts with PK (0x50 0x4B).
 */
export async function checkIfBlobIsZip(blob: Blob): Promise<boolean> {
	if (isZipContentType(null, blob.type)) {
		return true;
	}
	try {
		const slice = blob.slice(0, 4);
		const arrayBuffer = await slice.arrayBuffer();
		const bytes = new Uint8Array(arrayBuffer);
		return bytes.length >= 2 && bytes[0] === 0x50 && bytes[1] === 0x4b;
	} catch (error) {
		LoggerService.error("❌ [checkIfBlobIsZip] Error checking blob content", { error });
		return isZipContentType(null, blob.type);
	}
}

async function checkIfBlobIsPdf(blob: Blob): Promise<boolean> {
	// First check blob.type (fast check)
	if (blob.type === "application/pdf") {
		return true;
	}

	// If blob.type is not reliable, check the actual content
	try {
		const slice = blob.slice(0, 4);
		const arrayBuffer = await slice.arrayBuffer();
		const uint8Array = new Uint8Array(arrayBuffer);
		// Check if it starts with "%PDF"
		const isPdf =
			uint8Array.length >= 4 &&
			uint8Array[0] === 0x25 && // %
			uint8Array[1] === 0x50 && // P
			uint8Array[2] === 0x44 && // D
			uint8Array[3] === 0x46; // F

		if (isPdf && blob.type !== "application/pdf") {
			LoggerService.warn("⚠️ [checkIfBlobIsPdf] Blob is PDF but type is not application/pdf", {
				blobType: blob.type,
				firstBytes: Array.from(uint8Array)
					.map((b) => String.fromCharCode(b))
					.join(""),
			});
		}

		return isPdf;
	} catch (error) {
		LoggerService.error("❌ [checkIfBlobIsPdf] Error checking blob content", { error });
		// If we can't read the blob, fallback to blob.type
		return blob.type === "application/pdf";
	}
}
